import { buildDomainSeoBundle } from "./agenticSeoUtils";

export const EDUCATION_DOMAIN_INTRO = {
  title: "Education",
  content: "Agentic AI workflow automation for Education — across Higher Education, Schools and Universities, Universities, EdTech, Student Affairs, and Schools. Use cases span assignment rubric feedback, timetable generation, placement resume matching, LMS helpdesk assistance, student grievance routing, and parent communication. Practical deployments for institutions in India, UAE, KSA, UK, and USA with human-in-the-loop review, status tracking, and audit evidence.",
  seoDescription: "Education agentic AI use cases — higher education, schools universities, EdTech, student affairs. Assignment rubric feedback, timetable generation, placement resume matching, LMS helpdesk, student grievance routing, parent communication. LMS SIS CRM integration.",
};

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function withMeta(uc) { return { ...uc, groupId: slug(uc.subIndustry), seoTitle: uc.title, title: uc.shortTitle || uc.title }; }

const EDUCATION_SYSTEMS = "LMS, SIS, CRM, Email, Portal, Power BI";

const RAW = [
  {
    id: "assignment-rubric-feedback-support",
    subIndustry: "Higher Education",
    department: "Academics",
    businessProcess: "Assignment rubric feedback support",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Assignment rubric feedback support",
    title: "Assignment Rubric Feedback Support Automation for Education",
    seoKeywords: "education, assignment rubric feedback support, AI automation, RPA, agentic AI",
    challenge: "Faculty manually gives repetitive formative feedback against rubrics.",
    solution: "Implement AI Agent + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Higher Education teams can improve assignment rubric feedback support with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "AI Agent + Workflow Automation",
    buyerPersona: "Academic Head, Dean",
    systems: EDUCATION_SYSTEMS,
    agentsRequired: "Assignment AI Agent, Validation Agent, Human Review Agent",
    inputData: "Emails, forms, system records, task notes, master data and assignment rubric feedback support process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for assignment rubric feedback support",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "Medium",
  },
  {
    id: "timetable-generation",
    subIndustry: "Schools and Universities",
    department: "Administration",
    businessProcess: "Timetable generation",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Timetable generation",
    title: "Timetable Generation Automation for Education",
    seoKeywords: "education, timetable generation, AI automation, RPA, agentic AI",
    challenge: "Timetables are prepared manually while balancing faculty, room and course constraints.",
    solution: "Implement AI Agent + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Schools and Universities teams can improve timetable generation with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "AI Agent + Workflow Automation",
    buyerPersona: "Administration Head",
    systems: EDUCATION_SYSTEMS,
    agentsRequired: "Timetable AI Agent, Validation Agent, Human Review Agent",
    inputData: "Emails, forms, system records, task notes, master data and timetable generation process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for timetable generation",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "6-12 months",
    implementationComplexity: "High",
    businessImpact: "Medium",
  },
  {
    id: "placement-resume-matching",
    subIndustry: "Universities",
    department: "Placement",
    businessProcess: "Placement resume matching",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Placement resume matching",
    title: "Placement Resume Matching Automation for Education",
    seoKeywords: "education, placement resume matching, AI automation, RPA, agentic AI",
    challenge: "Placement teams manually match student resumes with job descriptions and eligibility rules.",
    solution: "Implement AI Agent + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Universities teams can improve placement resume matching with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "AI Agent + Workflow Automation",
    buyerPersona: "Placement Head",
    systems: EDUCATION_SYSTEMS,
    agentsRequired: "Placement AI Agent, Validation Agent, Human Review Agent",
    inputData: "Emails, forms, system records, task notes, master data and placement resume matching process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for placement resume matching",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "High",
  },
  {
    id: "lms-helpdesk-assistant",
    subIndustry: "EdTech",
    department: "Support",
    businessProcess: "LMS helpdesk assistant",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "LMS helpdesk assistant",
    title: "Lms Helpdesk Assistant Automation for Education",
    seoKeywords: "education, lms helpdesk assistant, AI automation, RPA, agentic AI",
    challenge: "Learners and faculty raise repeated LMS access, content and assessment queries.",
    solution: "Implement AI Agent + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "EdTech teams can improve lms helpdesk assistant with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "AI Agent + Workflow Automation",
    buyerPersona: "Support Head, CIO",
    systems: EDUCATION_SYSTEMS,
    agentsRequired: "LMS AI Agent, Validation Agent, Human Review Agent",
    inputData: "Emails, forms, system records, task notes, master data and lms helpdesk assistant process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for lms helpdesk assistant",
    keyBenefits: "Faster Response, Lower Service Workload, Better Customer Experience And Improved Sla Visibility",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
  {
    id: "student-grievance-routing",
    subIndustry: "Student Affairs",
    department: "Grievances",
    businessProcess: "Student grievance routing",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Student grievance routing",
    title: "Student Grievance Routing Automation for Education",
    seoKeywords: "education, student grievance routing, AI automation, RPA, agentic AI",
    challenge: "Student grievances are manually read, categorized and routed to departments.",
    solution: "Implement AI Agent + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Student Affairs teams can improve student grievance routing with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "AI Agent + Workflow Automation",
    buyerPersona: "Student Affairs Head",
    systems: EDUCATION_SYSTEMS,
    agentsRequired: "Student AI Agent, Validation Agent, Human Review Agent",
    inputData: "Emails, forms, system records, task notes, master data and student grievance routing process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for student grievance routing",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "3-6 months",
    implementationComplexity: "Medium",
    businessImpact: "Medium",
  },
  {
    id: "parent-communication-assistant",
    subIndustry: "Schools",
    department: "Parent Communication",
    businessProcess: "Parent communication assistant",
    regions: "India, UAE/Dubai, KSA/Saudi Arabia, UK, USA",
    shortTitle: "Parent communication assistant",
    title: "Parent Communication Assistant Automation for Education",
    seoKeywords: "education, parent communication assistant, AI automation, RPA, agentic AI",
    challenge: "Schools manually send reminders, circulars and responses across multiple channels.",
    solution: "Implement AI Agent + Workflow Automation to capture inputs, validate rules, route exceptions, generate summaries or actions, and update the target systems with human approval where required.",
    summary: "Schools teams can improve parent communication assistant with agentic AI workflow automation that captures inputs, validates rules, routes exceptions, and updates business systems with human approval where needed — reducing manual effort and improving audit trails.",
    automationType: "AI Agent + Workflow Automation",
    buyerPersona: "Principal, Administration Head",
    systems: EDUCATION_SYSTEMS,
    agentsRequired: "Parent AI Agent, Validation Agent, Human Review Agent",
    inputData: "Emails, forms, system records, task notes, master data and parent communication assistant process rules",
    outputActions: "Validated workflow record, task assignment, status update and exception report for parent communication assistant",
    keyBenefits: "Faster Turnaround, Lower Manual Effort, Improved Accuracy And Better Operational Visibility",
    roiTimeline: "0-3 months",
    implementationComplexity: "Low",
    businessImpact: "Medium",
  },
];
export const EDUCATION_USE_CASES = RAW.map(withMeta);
const GROUP_ORDER = ["Higher Education", "Schools and Universities", "Universities", "EdTech", "Student Affairs", "Schools"];
export const EDUCATION_GROUPS = GROUP_ORDER.map((label) => ({ id: slug(label), label })).filter((g) => EDUCATION_USE_CASES.some((uc) => uc.groupId === g.id));
const seo = buildDomainSeoBundle({ domainSlug: 'education-domain', domainIntro: EDUCATION_DOMAIN_INTRO, useCases: EDUCATION_USE_CASES });
export const EDUCATION_USE_CASES_SEO = seo.useCasesSeo;
export const EDUCATION_DOMAIN_FAQS = seo.domainFaqs;
export const EDUCATION_ITEM_LIST_JSONLD = seo.itemListJsonLd;
export const EDUCATION_FAQ_JSONLD = seo.faqJsonLd;
export const EDUCATION_BREADCRUMB_JSONLD = seo.breadcrumbJsonLd;
export const EDUCATION_WEBPAGE_JSONLD = seo.webPageJsonLd;

